// fallow-ignore-file boundary-violation
import { filter } from "lodash";

import { withAllContextHoc } from "app/components/React/HOCs/withAllContextsHOC";
import { rankTrackerFutureConfig } from "app/pages/digital-marketing/keywords-campaigns/user-route/futureStatesConfig";
import { ResearchRoot } from "app/pages/market-research/ResearchRoot";
import { fallback } from "app/routes/handlers/redirectFunctions/fallback";
import type { transitionHookFn } from "app/routes/handlers/redirectFunctions/redirectPipe";
import { redirectPipe } from "app/routes/handlers/redirectFunctions/redirectPipe";
import { SwTrack } from "app/services/SwTrack";
import { dashboardConfig } from "single-spa/react-app/config/legacy/dashboardConfig";
import { activationConfig } from "single-spa/react-app/config/solutions25/activation/activationConfig";
import { conversionConfig } from "single-spa/react-app/config/solutions25/conversionConfig";
import type { IRouterState, RouterStateConfig } from "single-spa/react-app/router/IRouterState";

import { SwLayout } from "../../../app/routes/SwLayout";

import { cigConfig } from "./legacy/cigConfig";
import { acquisitionConfig } from "./solutions25/acquisitionConfig";
import {
    advertiserActivityFutureConfig,
    searchAdvertisingFutureConfig,
    adTrackerFutureConfig,
    displayAdvertisingFutureConfig,
    monitorAndProtectFutureConfig,
    publisherAnalysisFutureConfig,
    socialChannelsFutureConfig,
    aiChannelsFutureConfig,
    llmsChannelsFutureConfig,
} from "./solutions25/adIntelligence/shared/futureStatesConfig";
import { brandMonitoringFutureConfig } from "./solutions25/campaigns/futureStatesConfig";
import { competitionConfig } from "./solutions25/competitionConfig";
import { digitalSuiteMarketResearchConfig } from "./solutions25/digitalSuiteConfig";
import { AIResearchFutureConfig } from "./solutions25/genAIIntelligence/AIResearch/futureStatesConfig";
import { AITrafficFutureConfig } from "./solutions25/genAIIntelligence/AITraffic/futureStatesConfig";
import { LLMTrackingConfig } from "./solutions25/genAIIntelligence/LLMTrackingConfig";
import { recommendationsFutureConfig } from "./solutions25/genAIIntelligence/Recommendations/futureStatesConfig";
import { premiumFeaturesConfig } from "./solutions25/premiumFeaturesConfig";
import {
    salesAdvertiserActivityFutureConfig,
    salesMonitorAndProtectFutureConfig,
    salesPublisherAnalysisFutureConfig,
} from "./solutions25/sales/adIntelligenceFutureConfig";
import { salesIntelligenceConfig } from "./solutions25/sales/salesIntelligenceConfig";
import { allTrackers } from "app/services/track/track";

const QUERY_PARAMS_KEY_REGEX = /([&?])(key)($|&|\?|"|')/gm; // find: ?key? &key? ?key& ?key& or ?key &key if it's the end of the string. excluded example: ?keyword &keyword

const swData = {
    menuKbItems(state: any) {
        const { pageId } = state;
        const tags = filter([pageId.section, pageId.subSection, pageId.subSubSection]).join("-");
        return tags;
    },
    educationBarTag(state: any) {
        const { pageId } = state;
        if (!pageId) {
            // to fix an issue where pageId is missing from state and caused the page to crash
            return undefined;
        }
        const tags = filter([pageId.section, pageId.subSection, pageId.subSubSection]).join("_").toUpperCase();
        return tags;
    },
    getCustomUrlType(toState: any) {
        // website | apps | keywords | industryAnalysis
        return this.getTrackId(toState)?.section;
    },
    getTrackId(toState: any, toParams?: any) {
        if (typeof toState.trackingId === "function") {
            return toState.trackingId(toParams);
        } else {
            return toState.trackingId || toState.pageId;
        }
    },
    pageViewTracking({ toParams, toState }: any) {
        // event param was added in order to differentiate between navChangeSuccess to navUpdate
        void SwTrack.trackPageView(toState, toParams);
    },
    disableRecording: false,
    showConnectedAccountsGlobalHook: false,
    showConnectedAccountsOnPageHook: false,
    showAvailableAppsIntro: false,
};

const DIGITAL_SUITE_TRAFFIC_AND_ENGAGEMENT_DUPLICATE_STATE = "digitalsuite_website_trafficandengagement_duplicate";

const onRedirectGoToNewURLStructure = (stateName: any): transitionHookFn => {
    allTrackers.dispatchTrackEvent("Internal Link", "click", `navigate/oldURL/${(stateName) || ""}`);
    return stateName;
};

const handleOldStructureURL = (configObj: RouterStateConfig) => {
    const oldConfig = Object.entries(configObj).reduce<RouterStateConfig>((acc, [state, stateObj]) => {
        //fix dri-4926
        if (state === DIGITAL_SUITE_TRAFFIC_AND_ENGAGEMENT_DUPLICATE_STATE) {
            return { ...acc };
        }
        if (stateObj.url) {
            let newURL: any = stateObj.url;
            const indexOfKeyInQueryParams = stateObj.url.search(QUERY_PARAMS_KEY_REGEX);
            if (indexOfKeyInQueryParams !== -1) {
                if (stateObj.url.endsWith("key")) {
                    newURL = newURL.substring(0, indexOfKeyInQueryParams);
                } else {
                    const urlLength = stateObj.url.length;
                    const partA = stateObj.url.substring(0, indexOfKeyInQueryParams);
                    const partB = stateObj.url.substring(indexOfKeyInQueryParams + 4, urlLength);
                    newURL = partA + partB;
                }
                newURL = newURL.replace("/:", "/:key/:");

                return {
                    ...acc,
                    [`${state}_old`]: {
                        ...stateObj,
                        url: newURL,
                        redirectTo: () => onRedirectGoToNewURLStructure(state),
                    } as IRouterState,
                };
            }
        }
        return { ...acc };
    }, {});
    return oldConfig;
};

export const statesConfig: RouterStateConfig = {
    sw: {
        name: "sw",
        abstract: true,
        configId: "Home",
        data: swData,
        component: withAllContextHoc(SwLayout),
    },
    research: {
        abstract: true,
        parent: "sw",
        component: withAllContextHoc(ResearchRoot),
        redirectTo: redirectPipe(
            fallback({
                digitalsuite: "digitalsuite-home",
            }),
        ),
    },
    // solution0
    ...cigConfig,
    ...dashboardConfig,
    // solution2.5
    ...conversionConfig,
    ...digitalSuiteMarketResearchConfig,
    ...handleOldStructureURL(competitionConfig),
    ...competitionConfig,
    ...acquisitionConfig,
    ...LLMTrackingConfig,
    ...premiumFeaturesConfig,
    ...salesIntelligenceConfig,
    ...activationConfig,
    ...advertiserActivityFutureConfig,
    ...displayAdvertisingFutureConfig,
    ...searchAdvertisingFutureConfig,
    ...publisherAnalysisFutureConfig,
    ...socialChannelsFutureConfig,
    ...aiChannelsFutureConfig,
    ...llmsChannelsFutureConfig,
    ...adTrackerFutureConfig,
    ...monitorAndProtectFutureConfig,
    ...rankTrackerFutureConfig,
    ...brandMonitoringFutureConfig,
    ...AITrafficFutureConfig,
    ...AIResearchFutureConfig,
    ...recommendationsFutureConfig,
    ...salesAdvertiserActivityFutureConfig,
    ...salesPublisherAnalysisFutureConfig,
    ...salesMonitorAndProtectFutureConfig,
};

export const allStates = Object.entries<any>(statesConfig).reduce((acc, [name, state]) => {
    if (state.pageId) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        acc[name] = {
            name,
            pageId: state.pageId,
            section: state.pageId.section,
            subSection: state.pageId.subSection,
            subSubSection: state.pageId.subSubSection,
            pageTitle: state.pageTitle,
            url: state.url,
            icon: state.icon,
            parent: state.parent,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            parentObj: statesConfig[state.parent],
            configId: state.configId,
            defaultQueryParams: state.defaultQueryParams,
        };
    }
    return acc;
});
